import { translateErrorCode } from '@/lib/ord-error-messages'

type ApiFetchOptions = RequestInit & { json?: unknown }

/**
 * Client-side fetch wrapper for Next.js app.
 * - Prefixes with NEXT_PUBLIC_BASE_PATH when provided
 * - Sends/accepts JSON by default when options.json is provided
 */
export async function apiFetch<T = any>(path: string, options?: ApiFetchOptions): Promise<T> {
    const base = (process.env.NEXT_PUBLIC_BASE_PATH || '') as string
    const url = path.startsWith('http') ? path : `${base}${path}`
    const init: RequestInit = { ...options }
    const headers: Record<string, string> = { ...(options?.headers as any) }

    if (typeof options?.json !== 'undefined') {
        headers['content-type'] = headers['content-type'] || 'application/json'
        headers['accept'] = headers['accept'] || 'application/json'
        init.body = typeof options.json === 'string' ? options.json : JSON.stringify(options.json)
        delete (init as any).json
    }
    init.headers = headers

    const resp = await fetch(url, init)
    const ct = resp.headers.get('content-type') || ''
    if (!resp.ok) {
        const text = await resp.text().catch(() => '')
        const msg = sanitizeUserFacingMessage(text, resp.status)
        throw new Error(msg)
    }
    if (ct.includes('application/json')) {
        return (await resp.json()) as T
    }
    return (await resp.text()) as unknown as T
}

export class ApiError extends Error {
    status: number
    body?: unknown
    code?: string
    traceId?: string
    constructor(status: number, message: string, body?: unknown) {
        super(message)
        this.name = 'ApiError'
        this.status = status
        this.body = body
        if (body && typeof body === 'object') {
            const anyBody = body as any
            if (typeof anyBody?.extensions?.code === 'string') this.code = anyBody.extensions.code
            if (typeof anyBody?.extensions?.traceId === 'string') this.traceId = anyBody.extensions.traceId
        }
    }
}

// Returns user-facing default message by HTTP status
function defaultMessageForStatus(status: number): string {
    if (status === 401) return 'Требуется вход'
    if (status === 403) return 'Доступ запрещён'
    if (status === 404) return 'Не найдено'
    if (status === 429) return 'Слишком много запросов'
    if (status === 400) return 'Неверные данные (обратитесь в поддержку)'
    if (status >= 500) return 'Ошибка сервера'
    if (status >= 400) return 'Ошибка запроса'
    return 'Ошибка сети'
}

// Very defensive sanitizer to avoid rendering raw HTML/huge blobs in UI
export function sanitizeUserFacingMessage(raw: unknown, status?: number): string {
    const numericStatus = Number(status || 0)
    const fallback = defaultMessageForStatus(numericStatus)
    if (raw == null) return fallback
    let txt = ''
    if (typeof raw === 'string') txt = raw
    else if (typeof raw === 'object') {
        try { txt = JSON.stringify(raw) } catch { txt = '' }
    }
    txt = String(txt || '').trim()
    if (!txt) return fallback

    // If looks like JSON, try to extract meaningful fields
    const looksLikeJson = (s: string) => (s.startsWith('{') || s.startsWith('[')) && s.length < 50_000
    if (looksLikeJson(txt)) {
        try {
            const parsed = JSON.parse(txt)
            const fromProblem = (o: any) =>
                (typeof o?.detail === 'string' && o.detail) ||
                (typeof o?.title === 'string' && o.title) ||
                (typeof o?.message === 'string' && o.message) ||
                (typeof o?.error === 'string' && o.error) ||
                ''
            let candidate = ''
            if (Array.isArray(parsed)) {
                // join first few string messages
                const items = parsed
                    .map((v) => typeof v === 'string' ? v : (typeof v === 'object' ? fromProblem(v) : ''))
                    .filter(Boolean)
                candidate = items.slice(0, 3).join(' • ')
            } else if (typeof parsed === 'object' && parsed) {
                // Problem+JSON or generic error object
                candidate = fromProblem(parsed)
                if (!candidate && typeof (parsed as any)?.errors === 'object') {
                    const errs = (parsed as any).errors
                    if (Array.isArray(errs)) candidate = errs.filter((x: any) => typeof x === 'string').join(' • ')
                    else candidate = Object.values(errs).flat().map((x: any) => String(x)).slice(0, 3).join(' • ')
                }
            }
            if (candidate && typeof candidate === 'string') txt = candidate
        } catch { /* ignore JSON parse errors */ }
    }

    // Машинный код ошибки без сопровождающего detail/message показывать нельзя:
    // пользователь увидел бы «counterparty_address_invalid_format».
    const translated = translateErrorCode(txt)
    if (translated) return translated

    // If it looks like HTML/Next error page or binary-like content — drop it
    const looksLikeHtml = /<!DOCTYPE|<html|<head|<body|<script|<meta|<link|_next\/static\/chunks/i.test(txt)
    if (looksLikeHtml) return fallback

    // Strip script/style blocks and HTML tags
    txt = txt.replace(/<script[\s\S]*?<\/script>/gi, ' ')
        .replace(/<style[\s\S]*?<\/style>/gi, ' ')
        .replace(/<[^>]+>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()

    // If still empty or suspicious
    if (!txt) return fallback

    // Truncate to prevent huge blobs
    const MAX_LEN = 220
    if (txt.length > MAX_LEN) {
        txt = txt.slice(0, MAX_LEN - 1).trimEnd() + '…'
    }
    return txt
}

function isAbsoluteUrl(url: string): boolean {
    return /^https?:\/\//i.test(url)
}

async function parseMaybeJson(resp: Response): Promise<unknown> {
    const contentType = resp.headers.get('content-type') || ''
    if (/application\/json/i.test(contentType)) {
        try { return await resp.json() } catch { return null }
    }
    try { return await resp.text() } catch { return null }
}

async function apiFetchJson<T>(path: string, init?: RequestInit): Promise<T> {
    const url = isAbsoluteUrl(path) ? path : path
    const headers: HeadersInit = {
        Accept: 'application/json',
        ...(init?.headers || {}),
    }
    // Автоматически добавляем JSON Content-Type для не-GET
    const method = (init?.method || 'GET').toUpperCase()
    const needsJson = method !== 'GET' && method !== 'HEAD'
    if (needsJson && !(headers as Record<string, string>)['Content-Type']) {
        ; (headers as Record<string, string>)['Content-Type'] = 'application/json'
    }

    const resp = await fetch(url, { ...init, headers })
    const body = await parseMaybeJson(resp)
    if (!resp.ok) {
        // RFC7807-aware error mapping
        let msg = defaultMessageForStatus(resp.status)
        if (body && typeof body === 'object') {
            const pb = body as any
            if (typeof pb?.title === 'string' && pb.title) msg = pb.title
            else if (typeof pb?.detail === 'string' && pb.detail) msg = pb.detail
        } else if (typeof body === 'string' && body.trim()) {
            msg = body
        }
        // Sanitize aggressively before surfacing to UI
        const safe = sanitizeUserFacingMessage(msg, resp.status)
        const err = new ApiError(resp.status, safe, body)
        // Здесь можно добавить глобальный логгер/тосты в вызывающем коде на основе err.code/err.traceId
        throw err
    }
    return (body as T) ?? ({} as T)
}

export const api = {
    get: async <T>(path: string, init?: RequestInit) => apiFetchJson<T>(path, { ...init, method: 'GET' }),
    post: async <T>(path: string, body?: unknown, init?: RequestInit) =>
        apiFetchJson<T>(path, { ...init, method: 'POST', body: body !== undefined ? JSON.stringify(body) : init?.body }),
    patch: async <T>(path: string, body?: unknown, init?: RequestInit) =>
        apiFetchJson<T>(path, { ...init, method: 'PATCH', body: body !== undefined ? JSON.stringify(body) : init?.body }),
    del: async <T>(path: string, init?: RequestInit) => apiFetchJson<T>(path, { ...init, method: 'DELETE' }),
}


